import React from 'react';
import { FiAlertTriangle, FiCheckCircle, FiInfo } from 'react-icons/fi';
import type {
  ChannelLeakage,
  ChannelLeakageDiagnosisKey,
  LeadSource,
} from '@troia-v3/types';

import { LeadSourceIcon } from '@/shared/icons/LeadSourceIcon';
import { DashboardSectionCard } from './DashboardSectionCard';

interface ChannelLeakageTableProps {
  channels: ChannelLeakage[];
}

const TROIA_PURPLE_DARK = '#7c3aed';

const DIAGNOSIS_META: Record<ChannelLeakageDiagnosisKey, { color: string; bg: string; icon: React.ReactNode }> = {
  healthy:       { color: '#10b981',          bg: 'rgba(16, 185, 129, 0.12)', icon: <FiCheckCircle size={11} /> },
  capture_low:   { color: '#f59e0b',          bg: 'rgba(245, 158, 11, 0.14)', icon: <FiAlertTriangle size={11} /> },
  cold_leads:    { color: '#f59e0b',          bg: 'rgba(245, 158, 11, 0.14)', icon: <FiAlertTriangle size={11} /> },
  cold_audience: { color: '#3b82f6',          bg: 'rgba(59, 130, 246, 0.12)', icon: <FiAlertTriangle size={11} /> },
  mixed:         { color: '#475569',          bg: 'rgba(148, 163, 184, 0.15)', icon: <FiInfo size={11} /> },
};

const formatNumber = (v: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(v);

const formatPct = (v: number): string =>
  `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(v)}%`;

/**
 * Regra híbrida C: roxo desbotado pra perda baixa, roxo médio pra perda média.
 * Vermelho discreto APENAS pra perda crítica (>=60%).
 */
const LossPill: React.FC<{ pct: number }> = ({ pct }) => {
  const isCritical = pct >= 60;
  const color = isCritical ? '#b91c1c' : pct >= 30 ? TROIA_PURPLE_DARK : '#94a3b8';
  const bg = isCritical
    ? 'rgba(239, 68, 68, 0.12)'
    : pct >= 30
      ? 'rgba(139, 92, 246, 0.10)'
      : 'rgba(139, 92, 246, 0.05)';
  return (
    <span
      className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full"
      style={{ backgroundColor: bg, color }}
    >
      {formatPct(pct)}
    </span>
  );
};

const SourcePill: React.FC<{ source: LeadSource; label: string }> = ({ source, label }) => (
  <span
    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
    style={{
      backgroundColor: 'rgba(139, 92, 246, 0.10)',
      color: TROIA_PURPLE_DARK,
      border: '1px solid rgba(139, 92, 246, 0.20)',
    }}
  >
    <LeadSourceIcon source={source} size={11} color={TROIA_PURPLE_DARK} />
    {label}
  </span>
);

const DiagnosisPill: React.FC<{ diagnosis: ChannelLeakageDiagnosisKey; label: string }> = ({ diagnosis, label }) => {
  const meta = DIAGNOSIS_META[diagnosis];
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
      style={{ backgroundColor: meta.bg, color: meta.color }}
    >
      {meta.icon}
      {label}
    </span>
  );
};

const headerCellStyle: React.CSSProperties = {
  backgroundColor: 'var(--theme-card-background)',
  color: 'var(--theme-muted-text)',
  borderBottom: '1px solid var(--theme-card-border)',
};

const cellStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--theme-card-border)',
  color: 'var(--theme-card-text)',
};

const HintBadge: React.FC = () => (
  <span className="text-[11px]" style={{ color: 'var(--theme-muted-text)' }}>
    % perdido em cada transição
  </span>
);

/** Card mobile de vazamento por canal. */
const LeakageMobileCard: React.FC<{ channel: ChannelLeakage }> = ({ channel }) => (
  <div
    className="rounded-lg p-3 flex flex-col gap-2.5"
    style={{
      backgroundColor: 'var(--theme-card-background)',
      border: '1px solid var(--theme-card-border)',
    }}
    data-testid={`channel-leakage-card-${channel.source}`}
  >
    <div className="flex items-center gap-2">
      <SourcePill source={channel.source} label={channel.label} />
      <span className="ml-auto">
        <DiagnosisPill diagnosis={channel.diagnosis} label={channel.diagnosisLabel} />
      </span>
    </div>
    <div className="grid grid-cols-2 gap-2">
      {[
        { k: 'V → L perdido', v: <LossPill pct={channel.visitToLeadLossPct} /> },
        { k: 'L → P perdido', v: <LossPill pct={channel.leadToProposalsLossPct} /> },
        { k: 'P → W perdido', v: <LossPill pct={channel.proposalsToWonLossPct} /> },
        {
          k: 'Total perdido',
          v: (
            <span
              className="text-sm font-bold"
              style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
            >
              {formatNumber(channel.totalLost)}
            </span>
          ),
        },
      ].map((cell) => (
        <div
          key={cell.k}
          className="rounded-md px-2.5 py-1.5"
          style={{ backgroundColor: 'var(--theme-page-background)' }}
        >
          <div className="text-[10px]" style={{ color: 'var(--theme-muted-text)' }}>{cell.k}</div>
          <div className="mt-1">{cell.v}</div>
        </div>
      ))}
    </div>
  </div>
);

export const ChannelLeakageTable: React.FC<ChannelLeakageTableProps> = ({ channels }) => {
  if (channels.length === 0) {
    return (
      <DashboardSectionCard
        icon={<FiAlertTriangle size={16} style={{ color: 'var(--theme-card-text)' }} />}
        title="Onde cada canal mais vaza"
        subtitle="Maior transição de perda por origem"
        badge={<HintBadge />}
        testId="channel-leakage-table"
      >
        <div
          className="rounded-lg p-8 text-center text-sm"
          style={{
            border: '1px dashed var(--theme-card-border)',
            color: 'var(--theme-muted-text)',
          }}
          data-testid="channel-leakage-table-empty"
        >
          Sem leads no período para comparar perdas por canal.
        </div>
      </DashboardSectionCard>
    );
  }

  return (
    <DashboardSectionCard
      icon={<FiAlertTriangle size={16} style={{ color: 'var(--theme-card-text)' }} />}
      title="Onde cada canal mais vaza"
      subtitle="Maior transição de perda por origem"
      badge={<HintBadge />}
      flush
      testId="channel-leakage-table"
    >
      {/* Tabela (md+) */}
      <div className="hidden min-[900px]:block" style={{ overflowX: 'auto' }}>
        <table className="w-full text-xs" style={{ minWidth: 720, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>Origem</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>V→L perdido</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>L→P perdido</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>P→W perdido</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>Total perdido</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>Diagnóstico</th>
            </tr>
          </thead>
          <tbody>
            {channels.map((channel) => (
              <tr key={channel.source} data-testid={`channel-leakage-row-${channel.source}`}>
                <td className="px-3 py-2" style={cellStyle}>
                  <SourcePill source={channel.source} label={channel.label} />
                </td>
                <td className="px-3 py-2 text-center" style={cellStyle}>
                  <LossPill pct={channel.visitToLeadLossPct} />
                </td>
                <td className="px-3 py-2 text-center" style={cellStyle}>
                  <LossPill pct={channel.leadToProposalsLossPct} />
                </td>
                <td className="px-3 py-2 text-center" style={cellStyle}>
                  <LossPill pct={channel.proposalsToWonLossPct} />
                </td>
                <td className="px-3 py-2 text-right font-semibold" style={cellStyle}>
                  {formatNumber(channel.totalLost)}
                </td>
                <td className="px-3 py-2" style={cellStyle}>
                  <DiagnosisPill diagnosis={channel.diagnosis} label={channel.diagnosisLabel} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cards (mobile) */}
      <div className="min-[900px]:hidden flex flex-col gap-2.5 p-4">
        {channels.map((channel) => (
          <LeakageMobileCard key={channel.source} channel={channel} />
        ))}
      </div>
    </DashboardSectionCard>
  );
};

ChannelLeakageTable.displayName = 'ChannelLeakageTable';
